import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import BrandingApplier from './components/BrandingApplier.jsx';
import { DESIGN_STUDIO_ALIASES } from './config/designStudioAliases.js';
import './App.scss';

// Shared design layout (the app shell: navbar + content outlet + footer). All
// seven design layouts resolve to this same shell; feature pages are generated
// per use case and mounted on their slug routes under it.
const DashboardLayout = lazy(() => import('./layouts/DashboardLayout.jsx'));
// The Customer Login page binds to the _patient-profile-setup-layout per its
// design reference; both layouts resolve to the same shared app shell.
const PatientProfileSetupLayout = lazy(() => import('./layouts/PatientProfileSetupLayout.jsx'));
// Chrome-less layout for the anonymous auth pages (no top navigation).
const AuthLayout = lazy(() => import('./layouts/AuthLayout.jsx'));
const RoleLanding = lazy(() => import('./pages/RoleLanding.jsx'));
const LoginPage = lazy(() => import('./pages/login/index.jsx'));
// Ticket 8406: Koral's Privacy Policy and Terms of Service, linked from every
// place the consents name them. Public — a patient must be able to read them
// before they have an account.
const LegalDocumentPage = lazy(() => import('./pages/legal/index.jsx'));
const RegisterPage = lazy(() => import('./pages/register/index.jsx'));
const ResetPasswordPage = lazy(() => import('./pages/reset-password/index.jsx'));
const SetNewPasswordPage = lazy(() => import('./pages/set-new-password/index.jsx'));
const StaffLoginPage = lazy(() => import('./pages/staff-login/index.jsx'));
const StaffResetPasswordPage = lazy(() => import('./pages/staff-reset-password/index.jsx'));
const StaffSetNewPasswordPage = lazy(() => import('./pages/staff-set-new-password/index.jsx'));
const DashboardPage = lazy(() => import('./pages/dashboard/index.jsx'));
const PatientProfileSetupPage = lazy(() => import('./pages/patient-profile-setup/index.jsx'));
const MedicalHistoryPage = lazy(() => import('./pages/medical-history/index.jsx'));
const DentalHistoryLifestylePage = lazy(() => import('./pages/dental-history-lifestyle/index.jsx'));
const SmileGoalsPage = lazy(() => import('./pages/smile-goals/index.jsx'));
const ConsentFormsPage = lazy(() => import('./pages/consent-forms/index.jsx'));
const OnboardingReviewSubmitPage = lazy(() => import('./pages/onboarding-review-submit/index.jsx'));
const AssessmentStartPage = lazy(() => import('./pages/assessment-start/index.jsx'));
const AssessmentPaymentPage = lazy(() => import('./pages/assessment-payment/index.jsx'));
const AssessmentProcessingPage = lazy(() => import('./pages/assessment-processing/index.jsx'));
const PaymentMockGatewayPage = lazy(() => import('./pages/payment-mock/index.jsx'));
const DentiCareMockGatewayPage = lazy(() => import('./pages/denticare-mock/index.jsx'));
const PaymentReturnPage = lazy(() => import('./pages/payment-return/index.jsx'));
const MySmileReviewPage = lazy(() => import('./pages/my-smile-review/index.jsx'));
// 8696 — the patient's Appointment Invitation; 8694 — the Notification Center.
const NotificationCenterPage = lazy(() => import('./pages/notification-center/index.jsx'));
// 8643 — Treatment Completion: Step 8's record, and the patient's way into Step 9.
const TreatmentCompletionPage = lazy(() => import('./pages/treatment-completion/index.jsx'));
// 8652 — the Retention Dashboard: Step 9's page, reached from Treatment Completion.
const RetentionDashboardPage = lazy(() => import('./pages/retention-dashboard/index.jsx'));
const GrowthDevelopmentPage = lazy(() => import('./pages/growth-development/index.jsx'));
const ManageAppointmentPage = lazy(() => import('./pages/manage-appointment/index.jsx'));
const AssessmentReportPage = lazy(() => import('./pages/assessment-report/index.jsx'));
const AiRunPage = lazy(() => import('./pages/ai-run/index.jsx'));
const ConsultationRequestPage = lazy(() => import('./pages/consultation-request/index.jsx'));
const ConsultationCheckoutPage = lazy(() => import('./pages/consultation-checkout/index.jsx'));
const ConsultationSchedulingPage = lazy(() => import('./pages/consultation-scheduling/index.jsx'));
const AccountManagementPage = lazy(() => import('./pages/account-management/index.jsx'));
const ClinicalReviewDashboardPage = lazy(() => import('./pages/clinical-review-dashboard/index.jsx'));
const DentistAssessmentReviewPage = lazy(() => import('./pages/dentist-assessment-review/index.jsx'));
const AiLearningRulesPage = lazy(() => import('./pages/ai-learning-rules/index.jsx'));
const AiImprovementsReviewPage = lazy(() => import('./pages/ai-improvements-review/index.jsx'));
const AiTrainingReadinessPage = lazy(() => import('./pages/ai-training-readiness/index.jsx'));
// Realignment 4.5 — the approved, versioned troubleshooting library.
const TroubleshootingLibraryPage = lazy(() => import('./pages/troubleshooting-library/index.jsx'));
const AppointmentDashboardPage = lazy(() => import('./pages/appointment-dashboard/index.jsx'));
const WorkbenchDashboardPage = lazy(() => import('./pages/workbench-dashboard/index.jsx'));
// Reached only from the Clinical Review Dashboard or the Workbench's Review
// button; deliberately absent from menuData.js (issue 8430 §1).
const TreatmentPlanPage = lazy(() => import('./pages/treatment-plan/index.jsx'));
const ChairSideWorkspacePage = lazy(() => import('./pages/chair-side-workspace/index.jsx'));
const UserAccountManagementPage = lazy(() => import('./pages/user-account-management/index.jsx'));
const RoleAssignmentManagementPage = lazy(() => import('./pages/role-assignment-management/index.jsx'));
const MyAccessPage = lazy(() => import('./pages/my-access/index.jsx'));
const SecuritySettingsPage = lazy(() => import('./pages/security-settings/index.jsx'));
const AppointmentTypeSettingsPage = lazy(() => import('./pages/appointment-type-settings/index.jsx'));
const ProductServiceCataloguePage = lazy(() => import('./pages/product-service-catalogue/index.jsx'));
const ProductServiceFormPage = lazy(() => import('./pages/product-service-catalogue-form/index.jsx'));
const TreatmentPackagesPage = lazy(() => import('./pages/treatment-packages/index.jsx'));
// Issue 8415: ADA Practice Fee Management, an independent Admin module.
const AdaPracticeFeeManagementPage = lazy(
  () => import('./pages/ada-practice-fee-management/index.jsx')
);
const TreatmentPackageFormPage = lazy(() => import('./pages/treatment-packages-form/index.jsx'));
const ClinicSettingsPage = lazy(() => import('./pages/clinic-settings/index.jsx'));
const ConversationInboxPage = lazy(() => import('./pages/conversation-inbox/index.jsx'));
const MessageHistoryReviewPage = lazy(() => import('./pages/message-history-review/index.jsx'));
const AssistantKnowledgePage = lazy(() => import('./pages/assistant-knowledge/index.jsx'));
// Realignment 3.5: the clinic's treatment consent clauses (versioned).
const ConsentTemplatePage = lazy(() => import('./pages/consent-template/index.jsx'));
// Realignment L6 follow-up: the data-use permission wording, drafted and approved by an admin.
const DataUseWordingPage = lazy(() => import('./pages/data-use-wording/index.jsx'));
const PatientLookupPage = lazy(() => import('./pages/patient-lookup/index.jsx'));
const PatientManagerPage = lazy(() => import('./pages/patient-manager/index.jsx'));
const PatientProfilePage = lazy(() => import('./pages/patient-profile/index.jsx'));
const BillingManagementPage = lazy(() => import('./pages/billing-management/index.jsx'));
const ClinicReportsDashboardPage = lazy(() => import('./pages/clinic-reports-dashboard/index.jsx'));
const ManualNotificationComposerPage = lazy(() => import('./pages/manual-notification-composer/index.jsx'));
const ClinicalSmileReviewPage = lazy(() => import('./pages/clinical-smile-review/index.jsx'));
const CaseDecisionViewerPage = lazy(() => import('./pages/case-decision-viewer/index.jsx'));
const AdminClientCaseManagementPage = lazy(() => import('./pages/admin-client-case-management/index.jsx'));

// Generic status pages.
const Unauthorized = lazy(() => import('./pages/status/Unauthorized.jsx'));
const Forbidden = lazy(() => import('./pages/status/Forbidden.jsx'));
const NotFound = lazy(() => import('./pages/status/NotFound.jsx'));
const ServerError = lazy(() => import('./pages/status/ServerError.jsx'));

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

/**
 * Redirect a Design-Studio alias to the screen that implements it, carrying the
 * query string across — several targets are context-dependent and read their
 * case from `?caseId=`, so dropping the search would turn a working deep link
 * into a bounce to the entry dashboard.
 */
function AliasRedirect({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrandingApplier />
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public (anonymous) routes — registered ahead of any guards so
                unauthenticated users can reach them. */}
            <Route element={<AuthLayout />}>
              <Route path="login" element={<LoginPage />} />
              <Route path="account/customer-registration" element={<RegisterPage />} />
              <Route path="account/reset-password" element={<ResetPasswordPage />} />
              <Route path="account/set-new-password" element={<SetNewPasswordPage />} />
              <Route path="account/staff-login" element={<StaffLoginPage />} />
              <Route path="account/staff-reset-password" element={<StaffResetPasswordPage />} />
              <Route path="account/staff-set-new-password" element={<StaffSetNewPasswordPage />} />
            </Route>

            {/* Ticket 8406: the legal documents the consents link to. Outside
                any layout so they read as documents, and outside the auth
                guard so the links resolve for anonymous visitors too. */}
            <Route path="legal/:slug" element={<LegalDocumentPage />} />

            {/* Shared app shell. Feature page routes (generated per use case)
                are added here as children, keyed on their menuData slug. */}
            <Route element={<DashboardLayout />}>
              <Route index element={<RoleLanding />} />

              {/* Authenticated feature pages — deny anonymous, redirect to login. */}
              <Route element={<ProtectedRoute />}>
                <Route path="patient-care/dashboard" element={<DashboardPage />} />
                <Route path="onboarding/patient-profile-setup" element={<PatientProfileSetupPage />} />
                <Route path="onboarding/medical-history" element={<MedicalHistoryPage />} />
                <Route
                  path="onboarding/dental-history-lifestyle-preferences"
                  element={<DentalHistoryLifestylePage />}
                />
                <Route path="onboarding/smile-goals-media-upload" element={<SmileGoalsPage />} />
                <Route path="onboarding/consent-forms" element={<ConsentFormsPage />} />
                <Route
                  path="onboarding/onboarding-review-submit"
                  element={<OnboardingReviewSubmitPage />}
                />
                <Route path="patient-care/checkout-review" element={<AssessmentStartPage />} />
                <Route path="patient-care/assessment-payment" element={<AssessmentPaymentPage />} />
                <Route
                  path="patient-care/assessment-processing"
                  element={<AssessmentProcessingPage />}
                />
                {/* My Smile Review — the patient's side of photo monitoring,
                    and Build V2's replacement for Patient Treatment
                    Monitoring. Reached from the Patient Dashboard. */}
                <Route path="patient-care/my-smile-review" element={<MySmileReviewPage />} />
                <Route path="patient-care/treatment-completion" element={<TreatmentCompletionPage />} />
                {/* 8875 / 8876: invitations are reviewed and confirmed on Manage
                    Appointment; links already sent to this page land there. */}
                <Route path="patient-care/appointment-invitation" element={<AliasRedirect to="/patient-care/manage-appointment" />} />
                <Route path="notifications" element={<NotificationCenterPage />} />
                <Route path="patient-care/retention-dashboard" element={<RetentionDashboardPage />} />
                {/* 8729 — the Growth & Development Program. */}
                <Route path="patient-care/growth-development" element={<GrowthDevelopmentPage />} />
                <Route path="patient-care/manage-appointment" element={<ManageAppointmentPage />} />
                <Route path="patient-care/assessment-report" element={<AssessmentReportPage />} />
                <Route path="patient-care/consultation-request" element={<ConsultationRequestPage />} />
                <Route path="patient-care/consultation-checkout" element={<ConsultationCheckoutPage />} />
                <Route path="patient-care/consultation-scheduling" element={<ConsultationSchedulingPage />} />
                <Route path="account/account-management" element={<AccountManagementPage />} />
                <Route
                  path="clinical-operations/clinical-review-dashboard"
                  element={<ClinicalReviewDashboardPage />}
                />
                <Route
                  path="clinical-operations/dentist-assessment-review"
                  element={<DentistAssessmentReviewPage />}
                />
                <Route
                  path="clinical-operations/appointment-dashboard"
                  element={<AppointmentDashboardPage />}
                />
                <Route
                  path="clinical-operations/workbench-dashboard"
                  element={<WorkbenchDashboardPage />}
                />
                <Route
                  path="clinical-operations/treatment-plan"
                  element={<TreatmentPlanPage />}
                />
                {/* AI transparency: one model run in full, opened from a provenance pill. */}
                <Route path="ai-runs/:id" element={<AiRunPage />} />
                <Route
                  path="clinical-operations/chair-side-workspace"
                  element={<ChairSideWorkspacePage />}
                />
                <Route
                  path="clinical-operations/patient-lookup"
                  element={<PatientLookupPage />}
                />
                {/* 8446: the patient-contextual workspace, opened from the
                    Workbench's Manage menu. Distinct from Patient Lookup
                    (population search) and Patient Profile (the record). */}
                <Route
                  path="clinical-operations/patient-manager"
                  element={<PatientManagerPage />}
                />
                {/* 8449: the patient's record and reference page, opened from
                    Patient Lookup. Its own route, because it is its own page —
                    it used to be a branch inside the lookup screen. */}
                <Route
                  path="clinical-operations/patient-profile"
                  element={<PatientProfilePage />}
                />
                {/* 8786: Billing & Insurance became Billing Management; the old
                    address forwards so a bookmark still lands on billing. */}
                <Route
                  path="clinical-operations/billing-management"
                  element={<BillingManagementPage />}
                />
                <Route
                  path="clinical-operations/billing-insurance"
                  element={<AliasRedirect to="/clinical-operations/billing-management" />}
                />
                <Route
                  path="reporting/clinic-reports-dashboard"
                  element={<ClinicReportsDashboardPage />}
                />
                <Route
                  path="administration/manual-notification-composer"
                  element={<ManualNotificationComposerPage />}
                />
                {/*
                  Design-Studio name aliases (see config/designStudioAliases.js).
                  The client's tracker names seven built screens differently from
                  the codebase; without these, every one of those names 404s.
                */}
                {DESIGN_STUDIO_ALIASES.map(({ alias, target }) => (
                  <Route key={alias} path={alias} element={<AliasRedirect to={target} />} />
                ))}

                {/* Clinical Smile Review — the clinician side of patient photo
                    monitoring, and Build V2's replacement for Dental
                    Monitoring. A queue across all patients, not a per-case
                    workspace, so it is reachable from the navigation and from
                    the Workbench. Role gating is the backend's (dentist/admin
                    on every /smile-reviews route) plus menuData's, as for every
                    other clinical page here. */}
                <Route
                  path="clinical-operations/clinical-smile-review"
                  element={<ClinicalSmileReviewPage />}
                />
                {/* Case Decision Viewer — a queue of cases opened with their
                    context pre-loaded, reached from the Workbench. */}
                <Route
                  path="clinical-operations/case-decision-viewer"
                  element={<CaseDecisionViewerPage />}
                />
                <Route
                  path="administration/user-account-management"
                  element={<UserAccountManagementPage />}
                />
                <Route
                  path="administration/role-assignment-management"
                  element={<RoleAssignmentManagementPage />}
                />
                <Route path="account/my-access" element={<MyAccessPage />} />
                <Route
                  path="administration/security-settings"
                  element={<SecuritySettingsPage />}
                />
                <Route
                  path="administration/appointment-type-settings"
                  element={<AppointmentTypeSettingsPage />}
                />
                <Route
                  path="administration/product-service-catalogue"
                  element={<ProductServiceCataloguePage />}
                />
                <Route
                  path="administration/product-service-catalogue-form"
                  element={<ProductServiceFormPage />}
                />
                <Route
                  path="administration/product-service-catalogue-form/:id"
                  element={<ProductServiceFormPage />}
                />
                <Route
                  path="administration/treatment-packages"
                  element={<TreatmentPackagesPage />}
                />
                <Route
                  path="administration/ada-practice-fee-management"
                  element={<AdaPracticeFeeManagementPage />}
                />
                <Route
                  path="administration/treatment-packages-form"
                  element={<TreatmentPackageFormPage />}
                />
                <Route
                  path="administration/treatment-packages-form/:id"
                  element={<TreatmentPackageFormPage />}
                />
                <Route
                  path="administration/clinic-settings"
                  element={<ClinicSettingsPage />}
                />
                <Route
                  path="communication/conversation-inbox"
                  element={<ConversationInboxPage />}
                />
                <Route
                  path="administration/message-history-review"
                  element={<MessageHistoryReviewPage />}
                />
                <Route path="administration/assistant-knowledge" element={<AssistantKnowledgePage />} />
                <Route path="administration/treatment-consent" element={<ConsentTemplatePage />} />
                <Route path="administration/data-use-wording" element={<DataUseWordingPage />} />
                <Route
                  path="administration/ai-learning-rules-configurator"
                  element={<AiLearningRulesPage />}
                />
                <Route
                  path="administration/ai-improvements-review-workspace"
                  element={<AiImprovementsReviewPage />}
                />
                <Route
                  path="administration/ai-training-readiness"
                  element={<AiTrainingReadinessPage />}
                />
                <Route
                  path="administration/troubleshooting-library"
                  element={<TroubleshootingLibraryPage />}
                />
                <Route
                  path="administration/admin-client-case-management"
                  element={<AdminClientCaseManagementPage />}
                />
                <Route path="payment/nab-mock" element={<PaymentMockGatewayPage />} />
                <Route path="payment/denticare-mock" element={<DentiCareMockGatewayPage />} />
                <Route path="payment/return" element={<PaymentReturnPage />} />
              </Route>

              {/* Generic status pages */}
              <Route path="401" element={<Unauthorized />} />
              <Route path="403" element={<Forbidden />} />
              <Route path="404" element={<NotFound />} />
              <Route path="500" element={<ServerError />} />

              {/* Catch-all — any unresolved route (including menu links whose
                  page component does not exist yet) lands on 404. */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
